import {
  type CalendarDate,
  parseDate,
  parseTime,
  type Time,
  today,
} from "@internationalized/date";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { selectTournamentSchema } from "@/db/schema";
import { useVenueFilterOptions } from "@/hooks/venues";
import { useTRPC } from "@/integrations/trpc/react";
import { getDefaultTimeZone } from "@/lib/dates";
import { calendarDateSchema, timeSchema } from "@/lib/schemas";

export type EditGeneralInfoFormProps = {
  tournamentId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function EditGeneralInfoForm({
  tournamentId,
  onOpenChange,
  onSuccess,
  ...props
}: EditGeneralInfoFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: tournament } = useSuspenseQuery({
    ...trpc.tournaments.get.queryOptions({ id: tournamentId }),
    select: (data) =>
      data
        ? {
            date: parseDate(data.date),
            startTime: parseTime(data.startTime),
            venueId: data.venueId,
            name: data.name,
            inviteOnly: data.inviteOnly,
            visible: data.visible,
            pointsMultiplier: data.pointsMultiplier,
          }
        : undefined,
  });

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.edit.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());

      if (onSuccess) {
        onSuccess();
      }

      onOpenChange(false);
    },
  });

  const schema = selectTournamentSchema
    .pick({ venueId: true, name: true, pointsMultiplier: true })
    .extend({
      date: calendarDateSchema().refine((date) => {
        return true;
      }),
      startTime: timeSchema(),
    });

  const form = useAppForm({
    defaultValues: {
      date: tournament?.date as CalendarDate,
      startTime: tournament?.startTime as Time,
      venueId: tournament?.venueId as number,
      name: (tournament?.name ?? null) as string | null,
      pointsMultiplier: tournament?.pointsMultiplier ?? 1,
      inviteOnly: tournament?.inviteOnly ?? false,
      visible: tournament?.visible ?? false,
      mergeDivisions: true,
    },
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({
      value: {
        date,
        startTime,
        venueId,
        name,
        pointsMultiplier,
        inviteOnly,
        visible,
        mergeDivisions,
      },
    }) => {
      mutate({
        id: tournamentId,
        date: date.toString(),
        startTime: startTime.toString(),
        venueId,
        name: name ?? null,
        pointsMultiplier,
        inviteOnly,
        visible,
        mergeDivisions,
      });
    },
  });

  const venueOptions = useVenueFilterOptions();

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Edit General Info
        </Heading>

        <form
          className="flex flex-col space-y-3"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to edit scheduling"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField
            name="venueId"
            children={(field) => (
              <field.ComboBox
                label="Location"
                field={field}
                isRequired={true}
                options={venueOptions.map(({ display, value }) => ({
                  display,
                  value,
                }))}
              />
            )}
          />

          <form.AppField
            name="date"
            children={(field) => (
              <field.DatePicker
                label="Date"
                field={field}
                isRequired={true}
                minValue={today(getDefaultTimeZone())}
              />
            )}
          />

          <form.AppField
            name="startTime"
            children={(field) => (
              <field.Time label="Start Time" isRequired={true} field={field} />
            )}
          />

          <form.AppField
            name="name"
            children={(field) => <field.Text label="Name" field={field} />}
          />

          <form.AppField
            name="pointsMultiplier"
            children={(field) => (
              <field.Number
                label="Points Multiplier"
                description="Set to 2 for tour stops. Multiplies juniors points for top-5 finishers."
                field={field}
                minValue={1}
                step={0.5}
                formatOptions={{ maximumFractionDigits: 1 }}
              />
            )}
          />

          <form.AppField
            name="inviteOnly"
            children={(field) => (
              <field.Checkbox label="Invite Only" field={field} />
            )}
          />

          <form.AppField
            name="visible"
            children={(field) => (
              <field.Checkbox label="Published" field={field} />
            )}
          />

          <form.AppField
            name="mergeDivisions"
            children={(field) => (
              <field.Checkbox
                label="Merge tournaments with same date and location."
                field={field}
              />
            )}
          />

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton requireChange={false}>Save</form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
